import { BadgeCheck, FlaskConical, ShieldCheck, Microscope, FileCheck2, Lock } from "lucide-react";

export default function Quality() {
  return (
    <div>
      <section className="bg-[var(--paper-2)] border-b border-[var(--line)]">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-16">
          <div className="eyebrow">Quality &amp; Testing</div>
          <h1 className="h-display text-4xl md:text-6xl mt-2 leading-[1.04]">Quality you can verify, not just trust.</h1>
          <p className="mt-4 max-w-2xl text-[var(--ink-soft)] text-[15px]">Every batch is independently tested by accredited US laboratories. We don't ask you to take our word for it — we publish the proof.</p>
        </div>
      </section>

      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        <Stat k="99%+" v="Identity Purity Guaranteed" />
        <Stat k="5" v="Quality Checks per Batch" />
        <Stat k="100%" v="Batch-Level Verification" />
      </section>

      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 pb-16 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card icon={Microscope} t="HPLC Analysis" d="Reverse-phase high-performance liquid chromatography confirms the principal peak area at the labeled concentration. Reported purity is the area-percent at 214 nm." />
        <Card icon={BadgeCheck} t="Mass Spectrometry" d="Identity is confirmed via mass spec — observed monoisotopic mass compared against the theoretical mass calculated from the declared sequence." />
        <Card icon={ShieldCheck} t="pH &amp; Stability Testing" d="Optimal pH and formulation testing ensures every batch remains intact through the labeled retest date." />
        <Card icon={FileCheck2} t="Per-batch COA" d="Every batch ships with a Koveralabs-verified Certificate of Analysis. Public batch search at the COA hub." />
        <Card icon={FlaskConical} t="Sterility &amp; Endotoxin" d="Third-party sterility and LAL testing confirms research compounds are free from bacterial and fungal contamination." />
        <Card icon={Lock} t="Batch QC Verification" d="Precision weighing and quality controls ensure same-spec quality from your first order to your fiftieth." />
      </section>

      <section className="relative py-16 overflow-hidden border-y border-[var(--line)]">
        <div className="absolute inset-0 starfield opacity-50" />
        <div className="nebula-blob cyan absolute" style={{ width: 500, height: 500, top: '-30%', left: '10%', opacity: 0.2 }} />
        <div className="nebula-blob magenta absolute" style={{ width: 400, height: 400, bottom: '-30%', right: '10%', opacity: 0.18 }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--terra)]">Independent · Third-Party</div>
          <div className="h-display text-3xl md:text-4xl mt-3 text-[var(--ink)]">Verified by <span className="gradient-text">Koveralabs</span> — no financial ties.</div>
          <p className="mt-4 text-[var(--ink-soft)]">Public reports tied to the batch on every product container. Built for fast verification, not buried disclosures.</p>
        </div>
      </section>
    </div>
  );
}

const Stat = ({ k, v }) => (
  <div className="border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-8 text-center">
    <div className="font-display text-6xl font-semibold text-[var(--terra)]">{k}</div>
    <div className="mt-2 text-sm uppercase tracking-[0.18em] font-mono text-[var(--ink-muted)]">{v}</div>
  </div>
);
const Card = ({ icon: Icon, t, d }) => (
  <div className="border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-6">
    <Icon className="h-6 w-6 text-[var(--terra)]" strokeWidth={1.6} />
    <div className="mt-4 font-display text-xl font-medium">{t}</div>
    <div className="mt-2 text-sm text-[var(--ink-soft)] leading-relaxed">{d}</div>
  </div>
);
